import type { CertificateClaimState } from '../domain/certificateCollection';
import { EMPTY_CERTIFICATE_CLAIMS, loadCertificateClaims, saveCertificateClaims } from '../domain/certificateCollection';

export interface CertificateSyncResult {
  claims: CertificateClaimState;
  added: number;
  synced: boolean;
}

function isClaimState(value: unknown): value is CertificateClaimState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<CertificateClaimState>;
  return Boolean(candidate.claims && typeof candidate.claims === 'object');
}

/** Local and remote are merged as a union, so no device can shorten the other. */
function mergeClaimState(local: CertificateClaimState, remote: CertificateClaimState): CertificateClaimState {
  const merge = (a: Record<string, string>, b: Record<string, string>) => {
    const merged: Record<string, string> = { ...a };
    for (const [key, value] of Object.entries(b)) {
      merged[key] = merged[key] && merged[key] < value ? merged[key] : value;
    }
    return merged;
  };
  return { claims: merge(local.claims, remote.claims), setRewards: merge(local.setRewards, remote.setRewards) };
}

/**
 * Uploads this device's claims and takes back the union of every device.
 *
 * A certificate the child already holds must survive a new phone, a cleared
 * browser or a failed request, so this only ever widens the local set. If the
 * server is unreachable the local claims are left exactly as they were.
 */
export async function syncCertificateClaims(childId: string): Promise<CertificateSyncResult> {
  const local = loadCertificateClaims(childId);
  if (!childId) return { claims: local, added: 0, synced: false };
  try {
    const response = await fetch('/api/passport/certificate-claims', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(local),
    });
    if (!response.ok) throw new Error(`CERTIFICATE_SYNC_${response.status}`);
    const payload: unknown = await response.json();
    if (!isClaimState(payload)) throw new Error('CERTIFICATE_SYNC_INVALID');
    const merged = mergeClaimState(local, { claims: payload.claims, setRewards: payload.setRewards ?? {} });
    saveCertificateClaims(childId, merged);
    const added = Object.keys(merged.claims).length - Object.keys(local.claims).length;
    return { claims: merged, added: Math.max(0, added), synced: true };
  } catch {
    // Offline or refused: keep what this device already granted.
    return { claims: local, added: 0, synced: false };
  }
}

export function emptyClaims(): CertificateClaimState {
  return EMPTY_CERTIFICATE_CLAIMS;
}
